import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { SubPage } from "@/components/sub-page";
import { supabase } from "@/integrations/supabase/client";
import equipo1 from "@/assets/equipo/equipo-1.jpg.asset.json";
import equipo2 from "@/assets/equipo/equipo-2.jpg.asset.json";
import equipo3 from "@/assets/equipo/equipo-3.jpg.asset.json";
import equipo4 from "@/assets/equipo/equipo-4.jpg.asset.json";

export const Route = createFileRoute("/quienes-somos/equipo")({
  head: () => ({
    meta: [
      { title: "Nuestro Equipo | Mine Class" },
      { name: "description", content: "Conoce al equipo multidisciplinario de Mine Class: expertos en minería, tecnología, capacitación e inteligencia artificial." },
      { property: "og:title", content: "Equipo Mine Class" },
      { property: "og:description", content: "Un equipo multidisciplinario detrás de Mine Class: minería, tecnología, capacitación e IA aplicada a la industria." },
      { property: "og:url", content: "/quienes-somos/equipo" },
    ],
    links: [{ rel: "canonical", href: "/quienes-somos/equipo" }],
  }),
  component: Page,
});

type Photo = { src: string; alt: string };

const defaultPhotos: Photo[] = [
  { src: equipo1.url, alt: "Equipo Mine Class - Encuentro corporativo" },
  { src: equipo2.url, alt: "Equipo Mine Class - Reunión de equipo" },
  { src: equipo3.url, alt: "Equipo Mine Class - Visita a mina en Rancagua" },
  { src: equipo4.url, alt: "Equipo Mine Class - Terreno minero Rancagua" },
];

function Page() {
  const [photos, setPhotos] = useState<Photo[]>(defaultPhotos);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("team_photos")
        .select("image_url, alt")
        .eq("is_active", true)
        .order("sort_order")
        .order("created_at");
      if (cancelled) return;
      if (data && data.length > 0) {
        setPhotos(data.map((p) => ({ src: p.image_url, alt: p.alt || "Equipo Mine Class" })));
      }
    })();
    return () => { cancelled = true; };
  }, []);


  const close = useCallback(() => setOpen(false), []);
  const next = useCallback(
    () => setIndex((i) => (i + 1) % photos.length),
    [photos.length],
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + photos.length) % photos.length),
    [photos.length],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close, next, prev]);

  return (
    <SubPage
      section="quienes-somos"
      eyebrow="Quiénes Somos"
      title="Nuestro Equipo"
      intro="Un equipo multidisciplinario de expertos en minería, tecnología e inteligencia artificial."
    >
      <div className="not-prose mx-auto max-w-3xl">
        <button
          type="button"
          onClick={() => {
            setIndex(0);
            setOpen(true);
          }}
          className="group relative block w-full overflow-hidden rounded-2xl border-2 border-[var(--color-brand-purple)] bg-[var(--color-brand-purple)]/5 transition-all duration-300 hover:shadow-2xl hover:shadow-[var(--color-brand-purple)]/20 focus:outline-none focus:ring-4 focus:ring-[var(--color-brand-coral)]/40"
          aria-label="Ver galería del equipo"
        >
          <div className="aspect-[16/10] w-full overflow-hidden bg-[var(--color-surface-container-low)]">
            <img
              src={photos[0].src}
              alt={photos[0].alt}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="eager"
            />
          </div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          <div className="pointer-events-none absolute bottom-4 left-4 right-4 flex items-center justify-between text-white opacity-0 transition-all duration-300 group-hover:opacity-100 md:bottom-6 md:left-6 md:right-6">
            <span className="font-mono-data text-xs uppercase tracking-widest md:text-sm">
              Galería · {photos.length} fotos
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-[var(--color-brand-coral)] px-4 py-2 text-xs font-bold uppercase tracking-wide md:text-sm">
              Ver más
              <span className="material-symbols-outlined text-base">arrow_forward</span>
            </span>
          </div>
        </button>

        <div className="mt-6 rounded-xl border-l-4 border-[var(--color-brand-coral)] bg-[var(--color-brand-coral)]/10 p-5 md:mt-8 md:p-6">
          <p className="text-base leading-relaxed text-[var(--color-text-primary)] md:text-lg">
            Conoce a las personas que hacen posible la experiencia Mine Class. Un equipo comprometido con la capacitación e innovación que la minería necesita.
          </p>
        </div>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Galería del equipo Mine Class"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm animate-[fade-in_0.25s_ease-out] p-4 md:p-8"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Cerrar galería"
            className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white backdrop-blur-md transition-all hover:bg-[var(--color-brand-coral)] hover:scale-110 md:right-6 md:top-6 md:h-12 md:w-12"
          >
            <span className="material-symbols-outlined">close</span>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Foto anterior"
            className="absolute left-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur-md transition-all hover:bg-[var(--color-brand-coral)] hover:scale-110 md:left-6 md:h-14 md:w-14"
          >
            <span className="material-symbols-outlined text-2xl md:text-3xl">chevron_left</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Foto siguiente"
            className="absolute right-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur-md transition-all hover:bg-[var(--color-brand-coral)] hover:scale-110 md:right-6 md:h-14 md:w-14"
          >
            <span className="material-symbols-outlined text-2xl md:text-3xl">chevron_right</span>
          </button>

          <div
            className="relative flex max-h-full w-full max-w-6xl flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full overflow-hidden rounded-xl bg-black shadow-2xl">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${index * 100}%)` }}
              >
                {photos.map((p, i) => (
                  <div
                    key={i}
                    className="flex min-w-full items-center justify-center"
                  >
                    <img
                      src={p.src}
                      alt={p.alt}
                      className="max-h-[75vh] w-full object-contain"
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {photos.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Ir a foto ${i + 1}`}
                  aria-current={i === index}
                  className={`h-2.5 rounded-full transition-all ${
                    i === index
                      ? "w-8 bg-[var(--color-brand-coral)]"
                      : "w-2.5 bg-white/40 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
            <p className="font-mono-data text-xs uppercase tracking-widest text-white/70">
              {index + 1} / {photos.length}
            </p>
          </div>
        </div>
      )}
    </SubPage>
  );
}
